import React, { useState, useEffect } from 'react';
import {
  Box,
  Fab,
  Grid,
  Paper,
  Typography,
  TextField,
  Button,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Card,
  CardContent,
  Chip,
  IconButton,
  InputAdornment,
  Avatar,
  Tooltip,
  Divider,
  Alert,
  Snackbar,
  Tabs,
  Tab,
  useMediaQuery,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Badge,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  LinearProgress,
  Stepper,
  Step,
  StepLabel,
  StepContent,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Search,
  Refresh,
  Restaurant,
  TrendingUp,
  Warning,
  TableRestaurant,
  Chair,
  LocalBar,
  WineBar,
  Coffee,
  CleaningServices,
  Build,
  CheckCircle,
  Cancel,
  Schedule,
  People,
  QrCodeScanner,
  Print,
  MoreVert,
  ArrowUpward,
  ArrowDownward,
  RestaurantMenu,
  RoomService,
  TakeoutDining,
  DeliveryDining,
  EventSeat,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Sidebar from '../../../../components/sidebars/Sidebar_hotelAdmin';
import TopNav from '../../../../components/TopNav_hotelStaff';

// Styled Components
const StatCard = styled(Card)(({ theme }) => ({
  background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${theme.palette.action.hover} 100%)`,
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: '12px',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: theme.shadows[8],
  },
}));

const StyledTabs = styled(Tabs)(({ theme }) => ({
  '& .MuiTabs-indicator': {
    backgroundColor: theme.palette.primary.main,
    height: 3,
    borderRadius: '3px 3px 0 0',
  },
}));

const StyledTab = styled(Tab)(({ theme }) => ({
  textTransform: 'none',
  fontWeight: 600,
  fontSize: '14px',
  minWidth: 120,
  color: theme.palette.text.secondary,
  '&.Mui-selected': {
    color: theme.palette.primary.main,
  },
}));

const FloatingActionButton = styled(Fab)(({ theme }) => ({
  position: 'fixed',
  bottom: theme.spacing(3),
  right: theme.spacing(3),
  zIndex: 1000,
}));

const TableCard = styled(Card)(({ theme, status }) => ({
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  position: 'relative',
  overflow: 'visible',
  border: `2px solid ${
    status === 'available' ? theme.palette.success.main :
    status === 'occupied' ? theme.palette.error.main :
    status === 'reserved' ? theme.palette.warning.main :
    status === 'cleaning' ? theme.palette.info.main :
    theme.palette.grey[500]
  }`,
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: theme.shadows[8],
  },
}));

const StatusIndicator = styled(Box)(({ theme, status }) => {
  const getColor = () => {
    switch(status) {
      case 'available': return theme.palette.success.main;
      case 'occupied': return theme.palette.error.main;
      case 'reserved': return theme.palette.warning.main;
      case 'cleaning': return theme.palette.info.main;
      case 'maintenance': return theme.palette.grey[600];
      default: return theme.palette.grey[500];
    }
  };
  return {
    width: 10,
    height: 10,
    borderRadius: '50%',
    backgroundColor: getColor(),
    display: 'inline-block',
    marginRight: 8,
  };
});

// Constants
const SECTIONS = [
   { value: 'Bar', label: '🍺 Bar/Skyline', icon: <WineBar />, color: '#FF9800' },
  { value: 'Lounge', label: '🍸Lounge/Arena', icon: <LocalBar />, color: '#9C27B0' },
 
   { value: 'Others', label: '📌 Others', icon: <TableRestaurant />, color: '#607D8B' },
];

const TABLE_STATUSES = [
  { value: 'available', label: 'Available', color: 'success', icon: <CheckCircle /> },
  { value: 'occupied', label: 'Occupied', color: 'error', icon: <Cancel /> },
  { value: 'reserved', label: 'Reserved', color: 'warning', icon: <Schedule /> },
  { value: 'cleaning', label: 'Cleaning', color: 'info', icon: <CleaningServices /> },
  { value: 'maintenance', label: 'Maintenance', color: 'default', icon: <Build /> },
];

// API URL
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const getCurrentStaffId = () => {
  const storedProfile = JSON.parse(localStorage.getItem('userProfile')) || {};
  return storedProfile.staff_id || storedProfile.id || null;
};

const getHotelCode = () => {
  try {
    const storedProfile = JSON.parse(localStorage.getItem('userProfile')) || {};
    return storedProfile.hotelcode || storedProfile.hotel_code || storedProfile.hotelCode || '';
  } catch (error) {
    console.error('Error getting hotel code:', error);
    return '';
  }
};

const HotelFBTableManagement = () => {
  const navigate = useNavigate();
  const isMobile = useMediaQuery('(max-width:768px)');
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [activeTab, setActiveTab] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedSection, setSelectedSection] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  
  // State management
  const [tables, setTables] = useState([]);
  const [sections, setSections] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(false);
  
  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(12);
  
  // Dialogs
  const [openDialog, setOpenDialog] = useState(false);
  const [openStatusDialog, setOpenStatusDialog] = useState(false);
  const [editingTable, setEditingTable] = useState(null);
  const [selectedTable, setSelectedTable] = useState(null);
  const [newStatus, setNewStatus] = useState('');
  
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [uploading, setUploading] = useState(false);

  // Form states
  const [tableForm, setTableForm] = useState({
    hotel_code: getHotelCode(),
    table_number: '',
    table_name: '',
    capacity: 2,
    section: 'Restaurant',
    status: 'available',
    created_by: getCurrentStaffId(),
  });

  // API service functions
  const api = {
    getTables: async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/hotel/F&B/f_b_tables.php`, {
          params: { 
            hotel_code: getHotelCode(),
            section: selectedSection,
            status: selectedStatus
          }
        });
        return response.data;
      } catch (error) {
        console.error('Error fetching tables:', error);
        throw new Error('Failed to fetch tables');
      }
    },

    createTable: async (tableData) => {
      try {
        const response = await axios.post(`${API_BASE_URL}/hotel/F&B/f_b_tables.php`, tableData);
        return response.data;
      } catch (error) {
        console.error('Error creating table:', error);
        throw new Error(error.response?.data?.error || 'Failed to create table');
      }
    },

    updateTable: async (tableData) => {
      try {
        const response = await axios.put(`${API_BASE_URL}/hotel/F&B/f_b_tables.php`, tableData);
        return response.data;
      } catch (error) {
        console.error('Error updating table:', error);
        throw new Error(error.response?.data?.error || 'Failed to update table');
      }
    },

   deleteTable: async (tableId) => {
  try {
    // Send DELETE request with query parameters instead of body
    const response = await axios.delete(`${API_BASE_URL}/hotel/F&B/f_b_tables.php`, {
      params: { 
        id: tableId, 
        hotel_code: getHotelCode() 
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error deleting table:', error);
    throw new Error(error.response?.data?.error || 'Failed to delete table');
  }
},
    updateTableStatus: async (tableId, status) => {
      try {
        const response = await axios.post(`${API_BASE_URL}/hotel/F&B/f_b_tables_status.php`, {
          id: tableId,
          hotel_code: getHotelCode(),
          status: status,
          updated_by: getCurrentStaffId()
        });
        return response.data;
      } catch (error) {
        console.error('Error updating table status:', error);
        throw new Error(error.response?.data?.error || 'Failed to update table status');
      }
    },
  };

  // Load data
  useEffect(() => {
    loadData();
  }, [selectedSection, selectedStatus]);

  const loadData = async () => {
    setLoading(true);
    try {
      const data = await api.getTables();
      if (data.success) {
        setTables(data.tables || []);
        setSections(data.sections || SECTIONS.map(s => s.value));
        setStatistics(data.statistics || null);
      }
    } catch (error) {
      console.error('Error loading data:', error);
      showSnackbar('Failed to load data: ' + error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  // Filtered data
  const filteredTables = tables.filter(table => {
    const matchesSearch = table.table_number?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          table.table_name?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesSection = !selectedSection || table.section === selectedSection;
    const matchesStatus = !selectedStatus || table.status === selectedStatus;
    return matchesSearch && matchesSection && matchesStatus;
  });

  const paginatedTables = filteredTables.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  // Dialog handlers
  const handleOpenDialog = (table = null) => {
    if (table) {
      setEditingTable(table);
      setTableForm({
        hotel_code: getHotelCode(),
        table_number: table.table_number,
        table_name: table.table_name || '',
        capacity: table.capacity,
        section: table.section,
        status: table.status,
        created_by: getCurrentStaffId(),
      });
    } else {
      setEditingTable(null);
      setTableForm({
        hotel_code: getHotelCode(),
        table_number: '',
        table_name: '',
        capacity: 2,
        section: 'Restaurant',
        status: 'available',
        created_by: getCurrentStaffId(),
      });
    }
    setOpenDialog(true);
  };

  const handleOpenStatusDialog = (table) => {
    setSelectedTable(table);
    setNewStatus(table.status);
    setOpenStatusDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingTable(null);
  };

  const handleCloseStatusDialog = () => {
    setOpenStatusDialog(false);
    setSelectedTable(null);
    setNewStatus('');
  };

  // Form handlers
  const handleTableInputChange = (field) => (event) => {
    setTableForm(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  // CRUD operations
  const handleSaveTable = async () => {
    if (!tableForm.table_number?.trim()) {
      showSnackbar('Please enter a table number', 'error');
      return;
    }
    
    if (!tableForm.section) {
      showSnackbar('Please select a section', 'error');
      return;
    }

    setUploading(true);
    try {
      let result;
      const submitData = {
        ...tableForm,
        capacity: parseInt(tableForm.capacity) || 2,
      };

      if (editingTable) {
        submitData.id = editingTable.id;
        submitData.updated_by = getCurrentStaffId();
        result = await api.updateTable(submitData);
      } else {
        result = await api.createTable(submitData);
      }

      if (result.success) {
        showSnackbar(editingTable ? 'Table updated!' : 'Table added!', 'success');
        await loadData();
        handleCloseDialog();
      } else {
        showSnackbar(result.error || 'Operation failed', 'error');
      }
    } catch (error) {
      console.error('Error saving table:', error);
      showSnackbar(error.message, 'error');
    } finally {
      setUploading(false);
    }
  };

  const handleUpdateStatus = async () => {
    if (!selectedTable || !newStatus) return;

    setUploading(true);
    try {
      const result = await api.updateTableStatus(selectedTable.id, newStatus);
      if (result.success) {
        showSnackbar(`Table ${selectedTable.table_number} status updated to ${newStatus}`, 'success');
        await loadData();
        handleCloseStatusDialog();
      } else {
        showSnackbar(result.error || 'Failed to update status', 'error');
      }
    } catch (error) {
      showSnackbar(error.message, 'error');
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteTable = async (tableId, tableNumber) => {
    if (window.confirm(`Are you sure you want to delete Table ${tableNumber}?`)) {
      try {
        const result = await api.deleteTable(tableId);
        if (result.success) {
          showSnackbar('Table deleted!', 'success');
          await loadData();
        } else {
          showSnackbar(result.error || 'Failed to delete table', 'error');
        }
      } catch (error) {
        showSnackbar('Failed to delete table: ' + error.message, 'error');
      }
    }
  };

  const showSnackbar = (message, severity) => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleRefresh = () => {
    loadData();
  };

  const toggleSidebar = () => setSidebarOpen((prev) => !prev);

  const getStatusColor = (status) => {
    const statusObj = TABLE_STATUSES.find(s => s.value === status);
    return statusObj?.color || 'default';
  };

  const getStatusIcon = (status) => {
    const statusObj = TABLE_STATUSES.find(s => s.value === status);
    return statusObj?.icon || <TableRestaurant />;
  };

  const getSectionIcon = (section) => {
    const sectionObj = SECTIONS.find(s => s.value === section);
    return sectionObj?.icon || <TableRestaurant />;
  };

  const getSectionColor = (section) => {
    const sectionObj = SECTIONS.find(s => s.value === section);
    return sectionObj?.color || '#607D8B';
  };

  // Tab content
  const renderTableView = () => (
    <Box>
      <Paper elevation={1} sx={{ p: 3, mb: 3, borderRadius: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Typography variant="h5">Tables ({tables.length})</Typography>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <TextField
              placeholder="Search tables..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              size="small"
              sx={{ minWidth: 200 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search color="action" />
                  </InputAdornment>
                ),
              }}
            />
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Section</InputLabel>
              <Select
                value={selectedSection}
                onChange={(e) => setSelectedSection(e.target.value)}
                label="Section"
              >
                <MenuItem value="">All Sections</MenuItem>
                {SECTIONS.map(section => (
                  <MenuItem key={section.value} value={section.value}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {section.icon}
                      {section.label}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
                label="Status"
              >
                <MenuItem value="">All Status</MenuItem>
                {TABLE_STATUSES.map(status => (
                  <MenuItem key={status.value} value={status.value}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {status.icon}
                      {status.label}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button 
              variant="contained" 
              startIcon={<Add />} 
              onClick={() => handleOpenDialog()}
            >
              Add Table
            </Button>
          </Box>
        </Box>
      </Paper>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Grid container spacing={3}>
            {paginatedTables.length === 0 ? (
              <Grid item xs={12}>
                <Paper sx={{ p: 4, textAlign: 'center' }}>
                  <Typography variant="body1" color="textSecondary">
                    No tables found
                  </Typography>
                </Paper>
              </Grid>
            ) : (
              paginatedTables.map((table) => (
                <Grid item xs={12} sm={6} md={4} lg={3} key={table.id}>
                  <TableCard status={table.status}>
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                        <Box>
                          <Typography variant="h5" fontWeight="bold">
                            Table {table.table_number}
                          </Typography>
                          {table.table_name && (
                            <Typography variant="body2" color="textSecondary">
                              {table.table_name}
                            </Typography>
                          )}
                        </Box>
                        <Chip
                          icon={getStatusIcon(table.status)}
                          label={TABLE_STATUSES.find(s => s.value === table.status)?.label || table.status}
                          size="small"
                          color={getStatusColor(table.status)}
                          sx={{ textTransform: 'capitalize' }}
                        />
                      </Box>

                      <Divider sx={{ my: 1.5 }} />

                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Avatar sx={{ width: 32, height: 32, bgcolor: getSectionColor(table.section) + '20', color: getSectionColor(table.section) }}>
                            {getSectionIcon(table.section)}
                          </Avatar>
                          <Typography variant="body2">
                            {table.section}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <People fontSize="small" color="action" />
                          <Typography variant="body2">
                            {table.capacity} seats
                          </Typography>
                        </Box>
                      </Box>

                      <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                        <Tooltip title="Change Status">
                          <IconButton 
                            size="small" 
                            color="primary"
                            onClick={() => handleOpenStatusDialog(table)}
                            sx={{ flex: 1 }}
                          >
                            <Schedule />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit Table">
                          <IconButton 
                            size="small" 
                            onClick={() => handleOpenDialog(table)}
                            sx={{ flex: 1 }}
                          >
                            <Edit />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete Table">
                          <IconButton 
                            size="small" 
                            color="error"
                            onClick={() => handleDeleteTable(table.id, table.table_number)}
                            sx={{ flex: 1 }}
                          >
                            <Delete />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </CardContent>
                  </TableCard>
                </Grid>
              ))
            )}
          </Grid>

          <TablePagination
            rowsPerPageOptions={[12, 24, 36, 48]}
            component="div"
            count={filteredTables.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            sx={{ mt: 2 }}
          />
        </>
      )}

      <FloatingActionButton color="primary" onClick={() => handleOpenDialog()}>
        <Add />
      </FloatingActionButton>
    </Box>
  );

  const renderAnalyticsTab = () => {
    const stats = statistics || {};
    
    const sectionStats = {};
    tables.forEach(table => {
      if (!sectionStats[table.section]) {
        sectionStats[table.section] = { total: 0, occupied: 0, available: 0, reserved: 0, capacity: 0 };
      }
      sectionStats[table.section].total++;
      sectionStats[table.section][table.status]++;
      sectionStats[table.section].capacity += table.capacity;
    });

    const totalCapacity = tables.reduce((sum, table) => sum + table.capacity, 0);
    const occupancyRate = stats.total_tables > 0 
      ? ((stats.occupied || 0) / stats.total_tables) * 100 
      : 0;

    return (
      <Box>
        <Typography variant="h4" gutterBottom color="primary" sx={{ mb: 3 }}>
          📊 Table Management Analytics
        </Typography>

        {/* Statistics Cards */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {[
            {
              title: 'Total Tables',
              value: stats.total_tables || 0,
              icon: <TableRestaurant />,
              color: 'primary',
              description: 'Total dining tables'
            },
            {
              title: 'Available',
              value: stats.available || 0,
              icon: <CheckCircle />,
              color: 'success',
              description: 'Ready for seating'
            },
            {
              title: 'Occupied',
              value: stats.occupied || 0,
              icon: <Cancel />,
              color: 'error',
              description: 'Currently in use'
            },
            {
              title: 'Reserved',
              value: stats.reserved || 0,
              icon: <Schedule />,
              color: 'warning',
              description: 'Booked tables'
            },
            {
              title: 'Total Capacity',
              value: totalCapacity,
              icon: <People />,
              color: 'info',
              description: 'Total seats available'
            },
            {
              title: 'Occupancy Rate',
              value: `${occupancyRate.toFixed(1)}%`,
              icon: <TrendingUp />,
              color: occupancyRate > 70 ? 'error' : occupancyRate > 40 ? 'warning' : 'success',
              description: 'Current table usage'
            },
          ].map((stat, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <StatCard>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography variant="h4" fontWeight="bold" color={`${stat.color}.main`}>
                        {stat.value}
                      </Typography>
                      <Typography variant="h6" sx={{ fontSize: '0.9rem' }}>
                        {stat.title}
                      </Typography>
                      <Typography variant="body2" color="textSecondary" sx={{ fontSize: '0.75rem' }}>
                        {stat.description}
                      </Typography>
                    </Box>
                    <Avatar sx={{ bgcolor: `${stat.color}.light`, width: 50, height: 50 }}>
                      {stat.icon}
                    </Avatar>
                  </Box>
                </CardContent>
              </StatCard>
            </Grid>
          ))}
        </Grid>

        <Grid container spacing={3}>
          {/* Section-wise Breakdown */}
          <Grid item xs={12} md={6}>
            <StatCard>
              <CardContent>
                <Typography variant="h6" gutterBottom color="primary">
                  🏠 Tables by Section
                </Typography>
                {Object.keys(sectionStats).length > 0 ? (
                  <Box>
                    {Object.entries(sectionStats).map(([section, data]) => (
                      <Box key={section} sx={{ mb: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {getSectionIcon(section)}
                            <Typography fontWeight="medium">{section}</Typography>
                          </Box>
                          <Typography variant="body2" color="textSecondary">
                            {data.total} tables • {data.capacity} seats
                          </Typography>
                        </Box>
                        <LinearProgress 
                          variant="determinate" 
                          value={(data.occupied / data.total) * 100 || 0}
                          sx={{ height: 8, borderRadius: 4, mb: 1 }}
                          color="error"
                        />
                        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                          <Chip size="small" icon={<CheckCircle />} label={`Available: ${data.available || 0}`} color="success" variant="outlined" />
                          <Chip size="small" icon={<Cancel />} label={`Occupied: ${data.occupied || 0}`} color="error" variant="outlined" />
                          <Chip size="small" icon={<Schedule />} label={`Reserved: ${data.reserved || 0}`} color="warning" variant="outlined" />
                        </Box>
                      </Box>
                    ))}
                  </Box>
                ) : (
                  <Typography variant="body2" color="textSecondary" sx={{ textAlign: 'center', py: 2 }}>
                    No section data available
                  </Typography>
                )}
              </CardContent>
            </StatCard>
          </Grid>

          {/* Status Distribution */}
          <Grid item xs={12} md={6}>
            <StatCard>
              <CardContent>
                <Typography variant="h6" gutterBottom color="primary">
                  📊 Status Distribution
                </Typography>
                <Box sx={{ mt: 2 }}>
                  {TABLE_STATUSES.map(status => {
                    const count = stats[status.value] || 0;
                    const percentage = stats.total_tables > 0 ? (count / stats.total_tables) * 100 : 0;
                    return (
                      <Box key={status.value} sx={{ mb: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {status.icon}
                            <Typography>{status.label}</Typography>
                          </Box>
                          <Typography variant="body2" color="textSecondary">
                            {count} tables ({percentage.toFixed(1)}%)
                          </Typography>
                        </Box>
                        <LinearProgress 
                          variant="determinate" 
                          value={percentage}
                          sx={{ height: 8, borderRadius: 4 }}
                          color={status.color}
                        />
                      </Box>
                    );
                  })}
                </Box>
              </CardContent>
            </StatCard>
          </Grid>

          {/* Table List Summary */}
          <Grid item xs={12}>
            <StatCard>
              <CardContent>
                <Typography variant="h6" gutterBottom color="primary">
                  📋 Table Directory
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ backgroundColor: 'grey.100' }}>
                        <TableCell><strong>Table #</strong></TableCell>
                        <TableCell><strong>Name</strong></TableCell>
                        <TableCell><strong>Section</strong></TableCell>
                        <TableCell><strong>Capacity</strong></TableCell>
                        <TableCell><strong>Status</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {tables.slice(0, 20).map((table) => (
                        <TableRow key={table.id} hover>
                          <TableCell>
                            <Typography fontWeight="medium">Table {table.table_number}</Typography>
                          </TableCell>
                          <TableCell>{table.table_name || '—'}</TableCell>
                          <TableCell>
                            <Chip 
                              size="small"
                              icon={getSectionIcon(table.section)}
                              label={table.section}
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell>{table.capacity} seats</TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              icon={getStatusIcon(table.status)}
                              label={TABLE_STATUSES.find(s => s.value === table.status)?.label}
                              color={getStatusColor(table.status)}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                {tables.length > 20 && (
                  <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                    Showing first 20 of {tables.length} tables
                  </Typography>
                )}
              </CardContent>
            </StatCard>
          </Grid>
        </Grid>

        {/* Refresh Button */}
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Button 
            variant="outlined" 
            startIcon={<Refresh />}
            onClick={handleRefresh}
            disabled={loading}
          >
            Refresh Analytics
          </Button>
        </Box>
      </Box>
    );
  };

  const tabLabels = [
    { label: 'Tables', icon: <TableRestaurant /> },
    { label: 'Analytics', icon: <TrendingUp /> },
  ];

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 0:
        return renderTableView();
      case 1:
        return renderAnalyticsTab();
      default:
        return renderTableView();
    }
  };

  return (
    <Box sx={{ display: 'flex' }}>
      <Sidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} />
      
      <Box
        sx={{
          flexGrow: 1,
          p: 3,
          ml: isMobile ? 0 : sidebarOpen ? 30 : 5,
          mt: 0,
          maxWidth: '1400px',
          mx: 'auto',
        }}
      >
        <TopNav
          onLogout={() => navigate('/HotelAdmin')}
          onNotificationClick={() => alert('Notifications')}
          toggleSidebar={toggleSidebar}
        />

        <Paper elevation={0} sx={{ p: 3, mt: 3, backgroundColor: 'primary.main', color: 'white', borderRadius: 2 }}>
          <Typography variant="h4" fontWeight="bold">
            🍽️ Table Management
          </Typography>
          <Typography variant="subtitle1">
            Manage dining tables, sections, and seating arrangements
          </Typography>
        </Paper>

        <Paper elevation={1} sx={{ mt: 2, borderRadius: 2 }}>
          <StyledTabs value={activeTab} onChange={handleTabChange}>
            {tabLabels.map((tab, index) => (
              <StyledTab 
                key={index} 
                label={
                  <Badge 
                    badgeContent={index === 0 ? tables.length : 0} 
                    color="primary"
                  >
                    {tab.label}
                  </Badge>
                } 
                icon={tab.icon} 
                iconPosition="start" 
              />
            ))}
          </StyledTabs>

          <Box sx={{ p: 3 }}>
            {renderTabContent()}
          </Box>
        </Paper>
      </Box>

      {/* Table Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingTable ? 'Edit Table' : 'Add New Table'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Table Number *"
                value={tableForm.table_number}
                onChange={handleTableInputChange('table_number')}
                required
                placeholder="e.g., 1, 2, 3, A1, B2"
                helperText="Unique identifier for this table"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Table Name (Optional)"
                value={tableForm.table_name}
                onChange={handleTableInputChange('table_name')}
                placeholder="e.g., Window View, VIP Section"
                helperText="Display name for the table"
              />
            </Grid>
            <Grid item xs={12} md={6}>
  <FormControl fullWidth>
    <InputLabel>Section *</InputLabel>
    <Select
      value={tableForm.section}
      onChange={handleTableInputChange('section')}
      label="Section *"
      sx={{ minWidth: 120 }}  // Add this line
    >
      {SECTIONS.map(section => (
        <MenuItem key={section.value} value={section.value}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {section.icon}
            {section.label}
          </Box>
        </MenuItem>
      ))}
    </Select>
  </FormControl>
</Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Capacity (Seats)"
                type="number"
                value={tableForm.capacity}
                onChange={handleTableInputChange('capacity')}
                InputProps={{ inputProps: { min: 1, max: 20 } }}
                helperText="Number of seats at this table"
              />
            </Grid>
            {editingTable && (
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={tableForm.status}
                    onChange={handleTableInputChange('status')}
                    label="Status"
                  >
                    {TABLE_STATUSES.map(status => (
                      <MenuItem key={status.value} value={status.value}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {status.icon}
                          {status.label}
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button 
            onClick={handleSaveTable}
            variant="contained"
            disabled={!tableForm.table_number || !tableForm.section || uploading}
          >
            {uploading ? <CircularProgress size={24} /> : (editingTable ? 'Update Table' : 'Create Table')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Update Status Dialog */}
      <Dialog open={openStatusDialog} onClose={handleCloseStatusDialog} maxWidth="xs" fullWidth>
        <DialogTitle>
          Update Table Status
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              Table {selectedTable?.table_number} - {selectedTable?.section}
            </Typography>
            <FormControl fullWidth sx={{ mt: 2 }}>
              <InputLabel>New Status</InputLabel>
              <Select
                value={newStatus}
                onChange={(e) => setNewStatus(e.target.value)}
                label="New Status"
              >
                {TABLE_STATUSES.map(status => (
                  <MenuItem key={status.value} value={status.value}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {status.icon}
                      {status.label}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseStatusDialog}>Cancel</Button>
          <Button 
            onClick={handleUpdateStatus}
            variant="contained"
            color="primary"
            disabled={!newStatus || newStatus === selectedTable?.status || uploading}
          >
            {uploading ? <CircularProgress size={24} /> : 'Update Status'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} variant="filled">
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default HotelFBTableManagement;